import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import {
  useDeleteWebsiteSource,
  useRefreshWebsiteSource,
  useWebsiteSources,
} from "@/features/documents/hooks/use-website-sources";
import type { WebsiteSource } from "@/features/documents/types";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  ArrowsClockwiseIcon,
  GlobeIcon,
  TrashIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

const STATUS_CLASSES: Record<WebsiteSource["status"], string> = {
  pending:
    "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  indexing: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  ready:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  failed: "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300",
};

/** The failures the server reports as codes (`INDEX_ERRORS` there). */
const KNOWN_ERRORS = ["unreachable", "no_pages", "off_domain"];

const displayUrl = (source: WebsiteSource) =>
  `${source.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}${source.path_prefix || ""}`;

const SourceRow = ({ source }: { source: WebsiteSource }) => {
  const { t } = useTranslation("documents");
  const locale = useDateFnsLocale();
  const refresh = useRefreshWebsiteSource();
  const remove = useDeleteWebsiteSource();
  const busy = source.status === "pending" || source.status === "indexing";

  const when = (at: number) =>
    formatDistanceToNow(new Date(at), { addSuffix: true, locale });

  const run = async (
    action: () => Promise<unknown>,
    success: string | ((result: unknown) => string),
  ) => {
    try {
      const result = await action();
      toast.success(typeof success === "string" ? success : success(result));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("website.messages.error"),
      );
    }
  };

  return (
    <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <GlobeIcon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={source.url}
              target="_blank"
              rel="noreferrer"
              className="truncate font-medium hover:underline"
            >
              {displayUrl(source)}
            </a>
            <Badge
              className={cn(
                "gap-1 border-transparent",
                STATUS_CLASSES[source.status],
              )}
            >
              {source.status === "indexing" && (
                <Spinner className="h-3 w-3 animate-spin" />
              )}
              {t(`website.status.${source.status}`)}
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground">
            {t("website.pages", { count: source.pages_count || 0 })}
            {" • "}
            {source.last_indexed_at
              ? t("website.lastIndexed", { when: when(source.last_indexed_at) })
              : t("website.neverIndexed")}
            {!busy && source.next_run_at > Date.now() && (
              <>
                {" • "}
                {t("website.nextRun", { when: when(source.next_run_at) })}
              </>
            )}
            {" • "}
            {t("website.maxPages", { count: source.max_pages })}
          </div>
          {source.status === "failed" && source.last_error && (
            <div className="flex items-center gap-1 text-xs text-destructive">
              <WarningCircleIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate" title={source.last_error}>
                {KNOWN_ERRORS.includes(source.last_error)
                  ? t(`website.errors.${source.last_error}`)
                  : source.last_error}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2 pl-8 sm:pl-0">
        <Button
          variant="outline"
          size="sm"
          disabled={busy || refresh.isPending}
          onClick={() =>
            run(
              () => refresh.mutateAsync(source.id),
              // Within the hour after a run, a refresh waits for the end of it.
              (result) => {
                const next =
                  (result as WebsiteSource | undefined)?.next_run_at || 0;
                return next > Date.now() + 30_000
                  ? t("website.messages.refreshScheduled", { when: when(next) })
                  : t("website.messages.refreshStarted");
              },
            )
          }
        >
          <ArrowsClockwiseIcon className="h-4 w-4" />
          {t("website.refresh")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          disabled={remove.isPending}
          confirm={t("website.messages.deleteConfirm", {
            url: displayUrl(source),
            count: source.pages_count || 0,
          })}
          onClick={() =>
            run(
              () => remove.mutateAsync(source.id),
              t("website.messages.deleteSuccess"),
            )
          }
        >
          <TrashIcon className="h-4 w-4" />
          <span className="sr-only sm:not-sr-only">{t("actions.delete")}</span>
        </Button>
      </div>
    </div>
  );
};

/** The websites the knowledge base reads; nothing at all until there is one. */
export const WebsiteSources = () => {
  const { t } = useTranslation("documents");
  const { data: sources = [] } = useWebsiteSources();

  if (sources.length === 0) return null;

  return (
    <section className="rounded-lg border bg-card px-4">
      <div className="border-b py-3">
        <h3 className="text-sm font-semibold">{t("website.title")}</h3>
        <p className="text-xs text-muted-foreground">
          {t("website.description")}
        </p>
      </div>
      <div className="divide-y">
        {sources.map((source) => (
          <SourceRow key={source.id} source={source} />
        ))}
      </div>
    </section>
  );
};
